import { ResponsiveBar } from "@nivo/bar";
import { patternLinesDef } from "@nivo/core";
import { DashboardLayout } from "components/DashboardLayout";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import Loader from "components/DesignSystem/Loader/loader";
import ToolTip from "components/design/toolTip";
import { Button } from "components/DesignSystem/Button/Button";
import Card from "components/DesignSystem/Card/Card";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import { Header } from "components/DesignSystem/Header/Header";
import Tab from "components/DesignSystem/Tab/Tab";
import { ExpenseRecognitionLocked } from "components/ExpenseRecognitionLocked/ExpenseRecognitionLocked";
import { Info } from "components/icons/Info";
import { LongArrowRight } from "components/icons/LongArrowRight";
import { colors } from "constants/colors";
import { YYYY_MM_DD } from "constants/date";
import dayjs from "dayjs";
import { Field, FieldProps, Formik } from "formik";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useFilters } from "hooks/useFilter";
import { usePageTitle } from "hooks/usePageTitle";
import { Route, Switch, useRouteMatch } from "react-router-dom";
import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";
import {
  ExpenseSchedule,
  useGetExpenseScheduleQuery,
} from "store/apis/expenseSchedule";
import { currency } from "utils/Currency";
import { formatTimeToMonthYear } from "utils/formatDate";
import { ExpenseWaterfall } from "./ExpenseWaterfall";
import { RecognitionPartyWaterfall } from "components/RecognitionPartyWaterfall/RecognitionPartyWaterfall";

const ExpenseScheduleChart = ({
  schedule,
}: {
  schedule: ExpenseSchedule[];
}) => {
  const chartData = schedule
    ?.filter(({ period }) => period !== "Prior Months")
    .map((item) => ({
      period: item.period,
      recognized_expense: item.recognized_expense?.total_recognized ?? 0,
      deferred_expenses: item.deferred_expense?.total_deferred ?? 0,
      period_start: item.period_start,
      period_end: item.period_end,
    }));

  function generateTicks(min: number, max: number, count: number) {
    const step = (max - min) / (count - 1);
    return Array.from({ length: count }, (_, i) => Math.round(min + i * step));
  }

  const minY = 0;
  const maxY = Math.max(
    ...chartData.map(
      (item) => Math.max(item.recognized_expense, item.deferred_expenses) + 50
    )
  );

  const isAllZero = chartData.every(
    (item) => item.recognized_expense === 0 && item.deferred_expenses === 0
  );
  const tickValues = isAllZero ? [0] : generateTicks(minY, maxY, 3);
  const markers = tickValues.map((value) => ({
    axis: "y" as const,
    value,
    lineStyle: { stroke: colors.neutral[0], strokeWidth: 1, zIndex: 1 },
    legendOrientation: "vertical" as const,
  }));

  return (
    <ResponsiveBar
      data={chartData}
      keys={["recognized_expense", "deferred_expenses"]}
      groupMode="grouped"
      indexBy="period"
      tooltip={(values) => {
        return (
          <div className="t-bg-neutral-100 t-text-surface t-p-3 t-rounded t-text-body-sm t-break-words t-hyphen-auto t-shadow-dark-30 t-text-left t-flex t-flex-col t-gap-1.5 t-w-44">
            <span>{values.data.period}</span>

            <div className="t-flex t-gap-1">
              <span className="t-rounded-full t-w-0.5 t-h-full  t-bg-purple-40">
                &nbsp;
              </span>
              <span>Recognised</span>
              <span className="t-ml-auto">
                <AmountSuperScript
                  amount={Number(values.data.recognized_expense || 0)}
                />
              </span>
            </div>
            <div className="t-flex t-gap-1">
              <span className="t-rounded-full t-w-0.5 t-h-full t-bg-neutral-20">
                &nbsp;
              </span>
              <span>Deferred</span>
              <span className="t-ml-auto">
                <AmountSuperScript
                  amount={Number(values.data.deferred_expenses || 0)}
                />
              </span>
            </div>
          </div>
        );
      }}
      markers={markers}
      enableGridY={false}
      axisLeft={{
        tickValues: tickValues,
        format: (value) => {
          return currency({ amount: value });
        },
      }}
      theme={{
        axis: {
          ticks: {
            line: {
              stroke: colors.neutral[0],
              strokeWidth: 1,
            },
            text: {
              fill: colors.neutral[90],
              fontSize: 12,
            },
          },
        },
      }}
      margin={{ top: 30, right: 30, bottom: 50, left: 90 }}
      innerPadding={2}
      padding={0.4}
      enableLabel={false}
      borderRadius={2}
      layers={["markers", "axes", "totals", "bars", "legends", "annotations"]}
      defs={[
        patternLinesDef("deferred_expenses-pattern", {
          spacing: 5,
          rotation: -45,
          lineWidth: 2,
          background: colors.neutral[20],
          color: colors.neutral[40],
        }),

        patternLinesDef("recognized_expense-pattern", {
          spacing: 5,
          rotation: -45,
          lineWidth: 2,
          background: colors.purple[40],
          color: colors.purple[50],
        }),
      ]}
      fill={[
        {
          match: ({
            data: {
              data: { period, period_start },
              id,
            },
          }) =>
            dayjs(period_start).isSame(dayjs(), "month") &&
            id === "recognized_expense",
          id: "recognized_expense-pattern",
        },
        {
          match: ({
            data: {
              data: { period_start },
              id,
            },
          }) =>
            dayjs(period_start).isSame(dayjs(), "month") &&
            id === "deferred_expenses",
          id: "deferred_expenses-pattern",
        },
      ]}
      colors={({ id }) => {
        if (id === "recognized_expense") return colors.purple[40];
        if (id === "deferred_expenses") return colors.neutral[20];
        return "#000";
      }}
      axisBottom={{
        tickValues: chartData
          .filter((_, index) => index === 0 || index === chartData.length - 1)
          .map((item) => item.period),
      }}
    />
  );
};

const ExpenseScheduleOverview = () => {
  const entityId = useCurrentEntityId();

  const {
    values: { startDate, endDate, summaryDateStart },
    updateFilter,
  } = useFilters({
    initialValue: {
      startDate: dayjs().startOf("year").format(YYYY_MM_DD),
      endDate: dayjs().endOf("month").format(YYYY_MM_DD),
      summaryDateStart: dayjs()
        .startOf("year")
        .startOf("month")
        .format(YYYY_MM_DD),
    },
  });

  const {
    data: expenseScheduleDataWithoutPrior,
    isLoading: isLoadingExpenseScheduleWithoutPrior,
  } = useGetExpenseScheduleQuery(
    {
      entityId: entityId,
      start_date: startDate,
      end_date: endDate,
      carry_forward_bills: false,
    },
    { skip: !entityId }
  );

  const { data: expenseScheduleData, isLoading: isLoadingExpenseSchedule } =
    useGetExpenseScheduleQuery(
      {
        entityId: entityId,
        start_date: startDate,
        end_date: endDate,
        carry_forward_bills: true,
      },
      { skip: !entityId }
    );

  const { data: summaryData } = useGetExpenseScheduleQuery(
    {
      entityId: entityId,
      start_date: summaryDateStart,
      end_date: dayjs(summaryDateStart).endOf("month").format(YYYY_MM_DD),
      carry_forward_bills: true,
    },
    { skip: !entityId }
  );
  const summarySchedule = summaryData?.schedule.at(-1);

  if (isLoadingExpenseSchedule || isLoadingExpenseScheduleWithoutPrior) {
    return (
      <div>
        <Loader />
      </div>
    );
  }

  return (
    <div className="t-pb-24">
      <Card.Root className="sm:!t-px-3">
        <Card.Header>
          <div className="t-flex t-justify-between t-items-center t-gap-4 t-w-full sm:t-flex-col sm:t-items-start sm:t-gap-2">
            <p className="t-m-0 t-text-subtitle-lg">Expense Overview</p>
            <div>
              <Formik
                onSubmit={() => {}}
                initialValues={{ startDate: "", endDate: "" }}
              >
                <div className="t-flex t-gap-2 t-items-center">
                  <Field name="startDate">
                    {({ field }: FieldProps) => (
                      <DateInput
                        {...field}
                        showMonthYearPicker
                        label="Start Date"
                        name="startDate"
                        value={startDate}
                        placeholder="Select start date"
                        minDate={dayjs().startOf("year").toDate()}
                        // maxDate={dayjs().endOf("month").toDate()}
                        customDatePickerInputTrigger={
                          <div>
                            <Button size="small">
                              {formatTimeToMonthYear(startDate)}
                            </Button>
                          </div>
                        }
                        onDateChange={(date) =>
                          updateFilter(
                            "startDate",
                            dayjs(date).startOf("month").format(YYYY_MM_DD)
                          )
                        }
                      />
                    )}
                  </Field>

                  <div className="t-text-text-30">
                    <LongArrowRight />
                  </div>

                  <Field name="endDate">
                    {({ field }: FieldProps) => (
                      <DateInput
                        {...field}
                        value={endDate}
                        showMonthYearPicker
                        popperPlacement="bottom-end"
                        label="End Date"
                        customDatePickerInputTrigger={
                          <div>
                            <Button size="small">
                              {formatTimeToMonthYear(endDate)}
                            </Button>
                          </div>
                        }
                        minDate={dayjs(startDate).toDate()}
                        // maxDate={dayjs().endOf("month").toDate()}
                        name="endDate"
                        placeholder="Select end date"
                        onDateChange={(date) =>
                          updateFilter(
                            "endDate",
                            dayjs(date).endOf("month").format(YYYY_MM_DD)
                          )
                        }
                      />
                    )}
                  </Field>
                </div>
              </Formik>
            </div>
          </div>
        </Card.Header>
        <Card.Body className="t-flex t-my-4 sm:t-flex-col">
          <div className="t-flex t-flex-col t-gap-6 t-flex-shrink-0 t-w-[150px] t-mr-6 sm:t-w-full sm:t-mr-0 sm:t-flex-row sm:t-justify-between sm:t-gap-3 sm:t-pb-4">
            <div className="t-flex t-flex-col t-gap-1">
              <span className="t-whitespace-nowrap t-flex t-items-center t-gap-1">
                <span className="t-text-body-sm t-text-text-30">Prepaid</span>
                <ToolTip
                  text={`Expense prepaid from ${formatTimeToMonthYear(
                    startDate
                  )} to ${formatTimeToMonthYear(endDate)}`}
                >
                  <span className="t-text-text-30 t-flex">
                    <Info size="14" stroke="1.4" />
                  </span>
                </ToolTip>
              </span>
              <span className="t-text-subtext t-text-text-60">
                <AmountSuperScript
                  amount={Number(
                    expenseScheduleDataWithoutPrior?.total_booked_expense || 0
                  )}
                />
              </span>
            </div>

            <div className="t-flex t-flex-col t-gap-1">
              <span className="t-whitespace-nowrap t-flex t-items-center t-gap-1">
                <span className="t-text-body-sm t-text-text-30 t-flex t-items-center t-gap-1">
                  <div className="t-w-1.5 t-h-1.5 t-bg-purple-40 t-rounded-full" />
                  <span className="t-text-body-sm t-text-text-30">Accrued</span>
                </span>
                <ToolTip
                  text={`Expense accrued from ${formatTimeToMonthYear(
                    startDate
                  )} to ${formatTimeToMonthYear(endDate)}`}
                >
                  <span className="t-text-text-30 t-flex">
                    <Info size="14" stroke="1.4" />
                  </span>
                </ToolTip>
              </span>
              <span className="t-text-subtext t-text-text-60">
                <AmountSuperScript
                  amount={Number(
                    expenseScheduleData?.total_recognized_expense || 0
                  )}
                />
              </span>
            </div>

            <div className="t-flex t-flex-col t-gap-1">
              <span className="t-whitespace-nowrap t-flex t-items-center t-gap-1">
                <span className="t-text-body-sm t-text-text-30 t-flex t-items-center t-gap-1">
                  <div className="t-w-1.5 t-h-1.5 t-bg-neutral-40 t-rounded-full" />
                  <span>Deferred</span>
                </span>
                <ToolTip
                  text={`Expense deferred as of ${formatTimeToMonthYear(
                    endDate
                  )}`}
                >
                  <span className="t-text-text-30 t-flex">
                    <Info size="14" stroke="1.4" />
                  </span>
                </ToolTip>
              </span>
              <span className="t-text-subtext t-text-text-60">
                <AmountSuperScript
                  amount={Number(
                    expenseScheduleData?.total_deferred_expense_as_of_end_date ||
                      0
                  )}
                />
              </span>
            </div>
          </div>
          <div className="t-border-solid t-border-neutral-0 t-border-l t-border-0 sm:t-hidden" />
          <div className="t-h-[300px] t-w-full t-flex-shrink">
            {expenseScheduleData && (
              <ExpenseScheduleChart schedule={expenseScheduleData?.schedule} />
            )}
          </div>
        </Card.Body>
      </Card.Root>

      {summarySchedule && (
        <Card.Root className="t-mt-6 sm:!t-px-3">
          <Card.Header>
            <div className="t-flex t-justify-between t-items-center t-gap-4 t-w-full sm:t-flex-col sm:t-items-start sm:t-gap-2">
              <p className="t-m-0 t-text-subtitle-lg">
                {summarySchedule?.period} Summary
              </p>
              <div className="t-text-body-sm t-text-text-60">
                <Formik
                  onSubmit={() => {}}
                  initialValues={{ startDate: "", endDate: "" }}
                >
                  <div className="t-flex t-gap-2 t-items-center">
                    <Field name="startDate">
                      {({ field }: FieldProps) => (
                        <DateInput
                          {...field}
                          showMonthYearPicker
                          value={summaryDateStart}
                          label="Start Date"
                          name="startDate"
                          popperPlacement="bottom-end"
                          placeholder="Select start date"
                          minDate={dayjs().startOf("year").toDate()}
                          // maxDate={dayjs().endOf("month").toDate()}
                          customDatePickerInputTrigger={
                            <div>
                              <Button size="small">
                                {formatTimeToMonthYear(summaryDateStart)}
                              </Button>
                            </div>
                          }
                          onDateChange={(date) => {
                            updateFilter(
                              "summaryDateStart",
                              dayjs(date).startOf("month").format(YYYY_MM_DD)
                            );
                          }}
                        />
                      )}
                    </Field>
                  </div>
                </Formik>
              </div>
            </div>
          </Card.Header>
          <Card.Body className="t-mb-4">
            <div className="t-space-y-6">
              {/* Pre-paid Expense */}
              <div>
                <h3 className="t-text-subtext t-text-text-100 t-mb-3">
                  Pre-paid Expense
                </h3>
                <div className="t-flex t-justify-between t-items-center sm:t-gap-3 t-py-2 t-border-t t-border-neutral-10 t-text-body-sm t-text-text-60 t-bg-neutral-0 t-px-3 t-rounded">
                  <span className="t-text-body-sm t-text-text-60">
                    Expense pre-paid in {summarySchedule?.period}
                  </span>
                  <span className="t-text-body-sm t-text-text-60">
                    <AmountSuperScript
                      amount={Number(
                        summarySchedule?.booked_expense
                          .booked_from_this_period_sum || 0
                      )}
                    />
                  </span>
                </div>
              </div>

              {/* Accrued Expense */}
              <div>
                <h3 className="t-text-subtext t-text-text-100 t-mb-3">
                  Accrued Expense
                </h3>
                <div>
                  <div className="t-ml-2">
                    <div className="t-flex t-justify-between t-items-center sm:t-gap-3 t-py-2">
                      <span className="t-text-body-sm t-text-text-60">
                        Expense recognised from {summarySchedule?.period}
                        's bills
                      </span>
                      <span className="t-text-body-sm t-text-text-60">
                        <AmountSuperScript
                          amount={Number(
                            summarySchedule?.recognized_expense
                              ?.recognized_from_this_month || 0
                          )}
                        />
                      </span>
                    </div>
                    <div className="t-flex t-justify-between t-items-center sm:t-gap-3 t-py-2">
                      <span className="t-text-body-sm t-text-text-60">
                        Expense previously deferred and recognised in{" "}
                        {summarySchedule?.period}
                      </span>
                      <span className="t-text-body-sm t-text-text-60">
                        <AmountSuperScript
                          amount={Number(
                            summarySchedule?.recognized_expense
                              ?.recognized_from_previous_months || 0
                          )}
                        />
                      </span>
                    </div>
                  </div>
                  <div className="t-flex t-justify-between t-items-center sm:t-gap-3 t-py-2 t-border-t t-border-neutral-10 t-text-body-sm t-text-text-60 t-bg-neutral-0 t-px-3 t-rounded">
                    <span>
                      Total expense recognised in {summarySchedule?.period}
                    </span>
                    <span>
                      <AmountSuperScript
                        amount={Number(
                          summarySchedule?.recognized_expense
                            ?.total_recognized || 0
                        )}
                      />
                    </span>
                  </div>
                </div>
              </div>

              {/* Deferred Expense */}
              <div>
                <h3 className="t-text-subtext t-text-text-100 t-mb-3">
                  Deferred Expense
                </h3>
                <div>
                  <div className="t-ml-2">
                    <div className="t-flex t-justify-between t-items-center sm:t-gap-3 t-py-2">
                      <span className="t-text-body-sm t-text-text-60">
                        Expense deferred as of previous month
                      </span>
                      <span className="t-text-body-sm t-text-text-60">
                        <AmountSuperScript
                          amount={Number(
                            summarySchedule?.deferred_expense
                              ?.deferred_from_previous_months || 0
                          )}
                        />
                      </span>
                    </div>
                    <div className="t-flex t-justify-between t-items-center sm:t-gap-3 t-py-2">
                      <span className="t-text-body-sm t-text-text-60">
                        Expense deferred from {summarySchedule?.period}
                        's bills
                      </span>
                      <span className="t-text-body-sm t-text-text-60">
                        <AmountSuperScript
                          amount={Number(
                            summarySchedule?.deferred_expense
                              ?.deferred_from_this_month || 0
                          )}
                        />
                      </span>
                    </div>
                    <div className="t-flex t-justify-between t-items-center sm:t-gap-3 t-py-2">
                      <span className="t-text-body-sm t-text-text-60">
                        Less expense previously deferred and recognised in{" "}
                        {summarySchedule?.period}
                      </span>
                      <span className="t-text-body-sm t-text-text-60">
                        <AmountSuperScript
                          amount={Number(
                            summarySchedule?.deferred_expense
                              ?.prev_deferred_and_recognized_in_current_month ||
                              0
                          )}
                        />
                      </span>
                    </div>
                  </div>
                  <div className="t-flex t-justify-between t-items-center sm:t-gap-3 t-py-2 t-border-t t-border-neutral-10 t-text-body-sm t-text-text-60 t-bg-neutral-0 t-px-3 t-rounded">
                    <span>
                      Total expense deferred as of {summarySchedule?.period}
                    </span>
                    <span>
                      <AmountSuperScript
                        amount={Number(
                          summarySchedule?.deferred_expense?.total_deferred || 0
                        )}
                      />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </Card.Body>
        </Card.Root>
      )}
    </div>
  );
};

export const ExpenseScheduleHome = () => {
  usePageTitle("Expense Recognition");
  const entityId = useCurrentEntityId();
  const { path } = useRouteMatch();
  const { link } = useConstructInternalLink();
  const { data: bookkeepingSettings, isLoading: isLoadingBookkeepingSettings } =
    useGetBookkeepingSettingsQuery({
      entityId: entityId,
    });

  if (isLoadingBookkeepingSettings) {
    return (
      <div>
        <Loader />
      </div>
    );
  }

  if (!bookkeepingSettings?.deferred_expenses_enabled) {
    return <ExpenseRecognitionLocked />;
  }

  return (
    <DashboardLayout
      header={
        <Header
          v2
          title="Expense Recognition"
          bottom={
            <Tab.Root value="MATCH">
              <Tab.List className="!t-mb-6 sm:!t-gap-5">
                <Tab.NavTrigger exact value={link("", { includeUrl: true })}>
                  Overview
                </Tab.NavTrigger>
                <Tab.NavTrigger
                  exact
                  value={link("/waterfall", { includeUrl: true })}
                >
                  Expense Waterfall
                </Tab.NavTrigger>
                <Tab.NavTrigger
                  exact
                  value={link("/vendors", { includeUrl: true })}
                >
                  By Vendor
                </Tab.NavTrigger>
              </Tab.List>
            </Tab.Root>
          }
        />
      }
    >
      <Switch>
        <Route exact path={path}>
          <ExpenseScheduleOverview />
        </Route>
        <Route exact path={`${path}/waterfall`}>
          <ExpenseWaterfall />
        </Route>
        <Route exact path={`${path}/vendors`}>
          <RecognitionPartyWaterfall kind="EXPENSE" />
        </Route>
      </Switch>
    </DashboardLayout>
  );
};
